import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAtom } from 'jotai';
import { useTracker } from '@archipro-website/tracker';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerTitle,
} from '@rocco/components/drawer';
import { Button } from '@rocco/components/button';
import { uiStateAtomLoginPromptSheetOpened } from '~/modules-rocco/root/states/ui-state-login-prompt-sheet-opened';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';
import { LOGIN_PROMPT_TITLE } from '~/modules-rocco/user/hook/use-login-prompt-on-page-view';

const SHEET_HEADER = 'Log in or sign up';
const SHEET_TITLE = 'Welcome to ArchiPro';

type CtaFlow = 'login' | 'signup';

/**
 * Mobile bottom-sheet variant of the site-wide page-view login prompt
 * (login_prompt_sheet_2026). Unlike the full-screen modal there is no focus
 * trap and the page behind stays fully interactive — the scrim is visual
 * only, so taps and scrolls pass through to the page while the sheet is
 * open. Dismiss via the close button or swipe-down; the CTAs hand over to
 * the standard login/sign-up modal.
 */
export const LoginPromptSheet = () => {
    const [opened, setOpened] = useAtom(uiStateAtomLoginPromptSheetOpened);

    if (!opened) {
        return null;
    }

    return <OpenedLoginPromptSheet onClose={() => setOpened(false)} />;
};

/** Mounted only while open so each show gets a fresh opened-at timestamp. */
const OpenedLoginPromptSheet = ({ onClose }: { onClose: () => void }) => {
    const tracker = useTracker();
    const { setLoginModal } = useSetLoginModal();
    const openedAt = useRef(Date.now());
    const outcomeLogged = useRef(false);

    const handleOpenChange = (open: boolean) => {
        if (open) {
            return;
        }
        if (!outcomeLogged.current) {
            outcomeLogged.current = true;
            const msVisible = Date.now() - openedAt.current;
            // ExtraData JSON + Source are the only payload keys that reach
            // the warehouse; the flat keys serve network QA.
            tracker.log('LoginPromptDismissed', {
                url: new URL(window.location.href),
                data: {
                    authSource: 'siteGate',
                    variant: 'sheet',
                    msVisible,
                    Source: 'siteGate',
                    ExtraData: JSON.stringify({
                        authSource: 'siteGate',
                        variant: 'sheet',
                        msVisible,
                    }),
                },
            });
        }
        onClose();
    };

    const handleCta = (flow: CtaFlow) => {
        outcomeLogged.current = true;
        tracker.log('LoginPromptCtaClick', {
            url: new URL(window.location.href),
            data: {
                authSource: 'siteGate',
                variant: 'sheet',
                flow,
                Source: 'siteGate',
                ExtraData: JSON.stringify({
                    authSource: 'siteGate',
                    variant: 'sheet',
                    flow,
                }),
            },
        });
        onClose();
        setLoginModal({
            authSource: 'siteGate',
            isOpen: true,
            title: LOGIN_PROMPT_TITLE,
            // This modal is the CTA's destination, not a second prompt: the
            // sheet already reported the impression and the click above.
            skipPromptEvent: true,
        });
    };

    return (
        <>
            {/* Visual-only scrim: separates the sheet from the page without
                blocking it — pointer events pass through so the page behind
                stays scrollable and clickable. */}
            {createPortal(
                <div
                    aria-hidden
                    data-testid="login-prompt-scrim"
                    className="pointer-events-none fixed inset-0 z-modalBackdrop animate-in bg-black/40 duration-300 fade-in"
                />,
                document.body,
            )}
            <Drawer
                open
                direction="bottom"
                modal={false}
                onOpenChange={handleOpenChange}
            >
                {/* Shares the gate sheet's surface tokens — radius, drag
                    handle, close placement, horizontal padding — so the two
                    read as one system. Behaviour stays its own: this one is
                    non-modal and hands over to the login modal. */}
                <DrawerContent
                    noOverlay
                    className="data-[vaul-drawer-direction=bottom]:rounded-t-2xl"
                    closeButtonClassName="top-4 right-5 md:top-4 md:right-5"
                >
                    <div className="flex flex-col px-6 pt-2 pb-8">
                        <span className="pr-10 text-sm text-gray-600">
                            {SHEET_HEADER}
                        </span>
                        <DrawerTitle className="mt-4 font-medium">
                            {SHEET_TITLE}
                        </DrawerTitle>
                        <DrawerDescription className="mt-2">
                            {LOGIN_PROMPT_TITLE}
                        </DrawerDescription>
                        <div className="mt-6 flex gap-3">
                            <Button
                                color="dark"
                                className="flex-1"
                                onClick={() => handleCta('signup')}
                            >
                                Sign up
                            </Button>
                            <Button
                                color="outline"
                                className="flex-1"
                                onClick={() => handleCta('login')}
                            >
                                Log in
                            </Button>
                        </div>
                    </div>
                </DrawerContent>
            </Drawer>
        </>
    );
};
